// Authoritative wagering snapshots are versioned independently from tenant policy.
function version(value) {
  const raw = String(value ?? "");
  return /^\d+$/.test(raw) ? raw.replace(/^0+(?=\d)/, "") : null;
}
function older(next, previous) {
  const a = version(next), b = version(previous);
  return !a || !b || a.length < b.length || (a.length === b.length && a < b);
}
export function validWageringStatus(status) {
  return !!status && ["CREDIT", "TURNOVER"].includes(status.turn_mode)
    && /^-?\d+(?:\.\d+)?$/.test(String(status.turn_over)) && Number.isFinite(Number(status.turn_over)) && Number(status.turn_over) >= 0
    && /^-?\d+(?:\.\d+)?$/.test(String(status.credit)) && Number.isFinite(Number(status.credit))
    && version(status.member_version) !== null && version(status.policy_revision) !== null
    && Number.isInteger(status.pending_bet_count) && status.pending_bet_count >= 0
    && (status.pending_wagering_count === undefined || (Number.isSafeInteger(status.pending_wagering_count) && status.pending_wagering_count >= 0))
    && typeof status.withdrawal_allowed === "boolean";
}
export function mergeWageringMember(current, incoming) {
  if (!incoming) return current;
  if (current?._id && incoming._id && current._id !== incoming._id) return current;
  const previous = current?.wagering_status, next = incoming.wagering_status;
  if (previous && (!validWageringStatus(next) || older(next.policy_revision, previous.policy_revision) || older(next.member_version, previous.member_version))) {
    return { ...current, ...incoming, credit: current.credit, turn_over: current.turn_over, user_agent: current.user_agent,
      withdraw_max: current.withdraw_max, available_type: current.available_type, wagering_status: previous };
  }
  if (validWageringStatus(next)) return { ...incoming, credit: Number(next.credit), turn_over: Number(next.turn_over) };
  return incoming;
}
export function isTurnSatisfied(member, credit = member?.credit, previousMember = null) {
  const status = member?.wagering_status;
  if (status !== undefined) {
    if (!validWageringStatus(status)) return false;
    if ((status.pending_wagering_count ?? 0) > 0 || status.withdrawal_block_reason === "WAGERING_PROCESSING") return false;
    const previous = previousMember?.wagering_status;
    if (previous && (older(status.policy_revision, previous.policy_revision) || older(status.member_version, previous.member_version))) return false;
    return status.withdrawal_allowed;
  }
  if (previousMember?.wagering_status) return false;
  return Number.isFinite(Number(credit)) && member?.turn_over !== undefined && member?.turn_over !== null
    && Number.isFinite(Number(member.turn_over)) && Number(credit) >= Number(member.turn_over);
}
export function turnoverLabel(member) {
  return member?.wagering_status?.turn_mode === "TURNOVER" ? "เทิร์นคงเหลือ" : "เครดิตเป้าหมาย";
}
export function turnoverValue(member) {
  const value = member?.wagering_status?.turn_over ?? member?.turn_over;
  return value !== null && value !== undefined && Number.isFinite(Number(value)) ? String(value) : "—";
}
export function turnoverMessage(member) {
  const status = member?.wagering_status;
  if (status && !validWageringStatus(status)) return "กำลังตรวจสอบข้อมูลเทิร์น กรุณาอัปเดตข้อมูล";
  if ((status?.pending_wagering_count ?? 0) > 0 || status?.withdrawal_block_reason === "WAGERING_PROCESSING") return "กำลังอัปเดตยอดเทิร์น";
  if (status?.pending_bet_count > 0) return "รอผลเดิมพันที่ใช้ทำเทิร์นก่อนถอน";
  if (status?.withdrawal_block_reason === "WAGERING_MEMBER_IMPORT_REQUIRED") return "กำลังตรวจสอบข้อมูลเทิร์น กรุณาอัปเดตข้อมูล";
  return isTurnSatisfied(member) ? "ผ่านเงื่อนไขเทิร์นแล้ว" : status?.turn_mode === "TURNOVER" ? "ทำเทิร์นให้ครบก่อนถอน" : "เครดิตยังไม่ถึงเป้าหมาย";
}
// Presentation only: withdrawal admission continues to use isTurnSatisfied.
function formatWageringAmount(value) {
  if (typeof value !== "string" && typeof value !== "number") return "—";
  if (!Number.isFinite(Number(value)) || Number(value) < 0) return "—";
  let raw = String(value);
  if (typeof value === "number" && /e/i.test(raw)) {
    const [coefficient, exponent] = raw.toLowerCase().split("e");
    const [integer, fraction = ""] = coefficient.split(".");
    const digits = integer + fraction;
    const point = integer.length + Number(exponent);
    raw = point <= 0 ? "0." + "0".repeat(-point) + digits
      : point >= digits.length ? digits + "0".repeat(point - digits.length)
      : digits.slice(0, point) + "." + digits.slice(point);
  }
  if (!/^\d+(?:\.\d+)?$/.test(raw)) return "—";
  const [integer, fraction = ""] = raw.split(".");
  const decimals = fraction.replace(/0+$/, "").padEnd(2, "0");
  return integer.replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + decimals;
}

export function getWageringDisplay(member) {
  const status = member?.wagering_status;
  const managed = status !== undefined;
  const valid = managed ? validWageringStatus(status)
    : formatWageringAmount(member?.turn_over) !== "—"
      && member?.credit !== null && member?.credit !== undefined
      && member?.credit !== "" && Number.isFinite(Number(member.credit));
  const knownMode = !managed || status?.turn_mode === "CREDIT" || status?.turn_mode === "TURNOVER";
  const mode = knownMode ? (status?.turn_mode === "TURNOVER" ? "เทิร์นโอเวอร์" : "เทิร์นเครดิต") : "เงื่อนไขเทิร์น";
  const base = { mode, label: knownMode ? turnoverLabel(member) : "ยอดเทิร์น", amount: valid ? formatWageringAmount(managed ? status.turn_over : member.turn_over) : "—" };
  if (!valid || status?.withdrawal_block_reason === "WAGERING_MEMBER_IMPORT_REQUIRED") {
    return { ...base, tone: "unknown", badge: "รอตรวจสอบ", message: "กำลังตรวจสอบข้อมูลเทิร์น กรุณาอัปเดตข้อมูล" };
  }
  if ((status?.pending_wagering_count ?? 0) > 0 || status?.withdrawal_block_reason === "WAGERING_PROCESSING") {
    return { ...base, tone: "pending", badge: "กำลังอัปเดต", message: "ระบบกำลังอัปเดตยอดเทิร์นจากการเล่นล่าสุด" };
  }
  if (status?.pending_bet_count > 0) {
    return { ...base, tone: "pending", badge: "รอผลเดิมพัน", message: "รอผลเดิมพันที่ใช้ทำเทิร์นก่อนถอน" };
  }
  if (isTurnSatisfied(member)) {
    return { ...base, tone: "success", badge: "ผ่านเงื่อนไขเทิร์น", message: "การถอนยังเป็นไปตามเงื่อนไขของเว็บ" };
  }
  return { ...base, tone: "required", badge: "ยังไม่ครบเงื่อนไข",
    message: status?.turn_mode === "TURNOVER" ? "ยอดเดิมพันจะลดเทิร์นคงเหลือจนถึง 0"
      : "เครดิตปัจจุบันต้องถึงเครดิตเป้าหมายก่อนถอน" };
}

